/**
 * Measures the stage, builds the tile plan, and pushes the resulting
 * subscription set to the media client — the single place where the density
 * policy meets the wire. The plan is diffed by signature so a re-render never
 * causes a resubscribe.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { QualityProfile } from '@cyphex/types';
import type { MediaClient, MediaParticipant } from '../media/contract';
import { columnsFor } from '../media/tileOrder';
import { buildTilePlan, type MeetingLayout, type TilePlan } from '../media/tilePlan';

const GAP_PX = 8;

/**
 * How long the stage has to hold a width before it is believed.
 *
 * The ResizeObserver already coalesces to one animation frame, which is the
 * right amount for a scroll-bar appearing and the wrong amount for a 320px
 * side rail: opening the in-call chat is a UI toggle, it animates, and a user
 * who opens it and shuts it again pays a full `applySubscriptions` round trip
 * each way if every intermediate width reaches the plan. A third of a second is
 * long enough to swallow a rail toggle and a window-snap, and short enough that
 * a deliberate resize still settles before the user has let go of the frame.
 */
const SETTLE_MS = 300;

export interface UseTilePlanInput {
  readonly client: MediaClient;
  readonly participants: readonly MediaParticipant[];
  readonly activeSpeakerIds: readonly string[];
  readonly localId: string;
  readonly screenShareOwnerId: string | null;
  readonly pinnedId: string | null;
  readonly layout: MeetingLayout;
  readonly profile: QualityProfile;
  readonly page: number;
  readonly videoSuspended: boolean;
  readonly showSelfTile: boolean;
}

const signature = (plan: TilePlan): string =>
  `${[...plan.plan.video].map(([id, layer]) => `${id}:${layer}`).sort().join(',')}|` +
  `${[...plan.plan.audio].sort().join(',')}|${plan.plan.screenShare}`;


export function useTilePlan(input: UseTilePlanInput): {
  plan: TilePlan;
  stageRef: React.RefObject<HTMLDivElement>;
} {
  const stageRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(1024);

  useEffect(() => {
    const node = stageRef.current;
    if (!node) return;
    let frame = 0;
    let timer = 0;
    /* The FIRST measurement is not a resize — it is the stage telling us how
       big it has always been, and delaying it would mean one plan built from
       the 1024 default and a second one 300ms later. Only later changes wait. */
    let measured = false;
    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width;
      if (next === undefined) return;
      const rounded = Math.round(next);
      cancelAnimationFrame(frame);
      window.clearTimeout(timer);
      if (!measured) {
        measured = true;
        frame = requestAnimationFrame(() => setWidth(rounded));
        return;
      }
      timer = window.setTimeout(() => setWidth(rounded), SETTLE_MS);
    });
    observer.observe(node);
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(timer);
      observer.disconnect();
    };
  }, []);

  const visible = useMemo(
    () => (input.showSelfTile ? input.participants : input.participants.filter((p) => !p.isLocal)),
    [input.participants, input.showSelfTile],
  );

  const plan = useMemo(() => {
    const columns = columnsFor(Math.min(visible.length, 9));
    const tileCssWidth = Math.max(160, (width - GAP_PX * (columns - 1)) / columns);
    const devicePixelRatio = typeof window === 'undefined' ? 1 : window.devicePixelRatio;
    return buildTilePlan({
      participants: visible,
      activeSpeakerIds: input.activeSpeakerIds,
      localId: input.localId,
      screenShareOwnerId: input.screenShareOwnerId,
      pinnedId: input.pinnedId,
      layout: input.layout,
      profile: input.profile,
      page: input.page,
      tileCssWidth: tileCssWidth,
      devicePixelRatio,
      videoSuspended: input.videoSuspended,
    });
  }, [
    visible, width, input.activeSpeakerIds, input.localId, input.screenShareOwnerId,
    input.pinnedId, input.layout, input.profile, input.page, input.videoSuspended,
  ]);

  const applied = useRef('');
  useEffect(() => {
    const next = signature(plan);
    if (next === applied.current) return;
    applied.current = next;
    input.client.applySubscriptions(plan.plan);
  }, [plan, input.client]);

  return { plan, stageRef };
}
